/**
 * rPI Porter — Inbox Thread Detail (E-102 P1-7b live wire-up)
 *
 * Wires the Phase 0 Sally prototype to live API:
 *  - GET    /api/v1/threads/:id          (P1-4) — detail + messages
 *  - POST   /api/v1/threads/:id/reply    (P1-5) — outbound reply (TRIZ optimistic)
 *  - PATCH  /api/v1/threads/:id          (P1-5) — status / assignee / partner
 *
 * Sally UX round 2 refinements baked in:
 *  1. Partner auto-link NULL → "Ismeretlen kontakt" + [+ Partner létrehozása] CTA
 *  2. Long message (>6 lines or >500 chars) → truncated with expand/collapse
 *  3. Attachment 15-min presigned URL → refresh-on-error via <img onError>
 *  4. Assignee avatar-pill toggle (max 3 chips), NOT a dropdown
 *  5. NO draft save — Phase 2 scope (localStorage auto-save)
 *
 * TRIZ optimistic send pattern (matches P1-5 backend):
 *  - Click Send → UI bubble appears with syncStatus=PENDING_SYNC pill (grey)
 *  - 201 response → bubble flips to SYNCED (or disappears on next poll)
 *  - 502 response → bubble shows FAILED pill + [Újra] retry button
 */

import type { OmniMessage, ThreadChannel, ThreadStatus } from '@/api/porter-threads';
import { Button } from '@/components/ui';
import { usePatchThread, usePorterThread, useReplyToThread } from '@/hooks/use-porter-thread';
import type { OmniAttachment } from '@kgc/api-contract';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const TEAL = '#14B8A6';
const LONG_MESSAGE_LINE_THRESHOLD = 6;
const LONG_MESSAGE_CHAR_THRESHOLD = 500;

// Mock assignee roster — real roster (P2) comes from /api/v1/users?role=OWNER.
// Hardcoded short-list lets the avatar-pill pattern (Sally UX #4) ship now.
interface AssigneeOption {
  id: string;
  name: string;
  initials: string;
}

const MOCK_ASSIGNEES: readonly AssigneeOption[] = [
  { id: 'user-zoli', name: 'Zoli', initials: 'Z' },
  { id: 'user-zsuzsi', name: 'Zsuzsi', initials: 'ZS' },
  { id: 'user-gabor', name: 'Gábor', initials: 'G' },
];

// ============================================
// HELPERS
// ============================================

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'most';
  if (mins < 60) return `${mins} perce`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} órája`;
  const days = Math.round(hrs / 24);
  return `${days} napja`;
}

function isLongMessage(content: string): boolean {
  return (
    content.split('\n').length > LONG_MESSAGE_LINE_THRESHOLD ||
    content.length > LONG_MESSAGE_CHAR_THRESHOLD
  );
}

function truncateMessage(content: string): string {
  const lines = content.split('\n').slice(0, LONG_MESSAGE_LINE_THRESHOLD);
  const joined = lines.join('\n');
  return joined.length > LONG_MESSAGE_CHAR_THRESHOLD
    ? `${joined.slice(0, LONG_MESSAGE_CHAR_THRESHOLD)}…`
    : joined;
}

// ============================================
// SUB-COMPONENTS
// ============================================

function ChannelChip({ type }: { type: ThreadChannel }) {
  const { t } = useTranslation();
  const iconMap: Record<ThreadChannel, string> = {
    EMAIL: '📧',
    WEBCHAT: '💬',
    CONTACT_FORM: '📝',
  };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-teal-400/40 bg-teal-500/10 px-2 py-0.5 text-xs font-medium text-teal-700 dark:text-teal-300">
      <span>{iconMap[type]}</span>
      <span>{t(`porter.thread.channel.${type}`)}</span>
    </span>
  );
}

function StatusSelect({
  value,
  onChange,
  disabled,
}: {
  value: ThreadStatus;
  onChange: (next: ThreadStatus) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const statusClasses: Record<ThreadStatus, string> = {
    OPEN: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-400/40',
    PENDING: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-400/40',
    RESOLVED: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-400/40',
    SNOOZED: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-400/40',
  };
  return (
    <label
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold ${statusClasses[value]}`}
      aria-label={t('porter.thread.status.label')}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      <select
        value={value}
        onChange={e => onChange(e.target.value as ThreadStatus)}
        disabled={disabled}
        className="appearance-none bg-transparent pr-3 text-xs font-semibold outline-none"
        aria-label={t('porter.thread.status.label')}
      >
        <option value="OPEN">{t('porter.thread.status.OPEN')}</option>
        <option value="PENDING">{t('porter.thread.status.PENDING')}</option>
        <option value="RESOLVED">{t('porter.thread.status.RESOLVED')}</option>
        <option value="SNOOZED">{t('porter.thread.status.SNOOZED')}</option>
      </select>
    </label>
  );
}

function AssigneePills({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="inline-flex items-center gap-1"
      role="group"
      aria-label={t('porter.thread.assignee.label')}
    >
      {MOCK_ASSIGNEES.slice(0, 3).map(user => {
        const active = value === user.id;
        return (
          <button
            key={user.id}
            type="button"
            onClick={() => onChange(active ? null : user.id)}
            aria-pressed={active}
            title={user.name}
            className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold transition-all ${
              active
                ? 'bg-teal-500/20 text-teal-700 ring-2 ring-teal-500 dark:text-teal-300'
                : 'bg-slate-500/10 text-muted-foreground hover:bg-slate-500/20'
            }`}
          >
            {user.initials}
          </button>
        );
      })}
      {value === null && (
        <span className="ml-1 text-xs text-muted-foreground">
          {t('porter.thread.assignee.unassigned')}
        </span>
      )}
    </div>
  );
}

function AttachmentImage({ attachment }: { attachment: OmniAttachment }) {
  const { t } = useTranslation();
  const [errored, setErrored] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Sally UX #3 — presigned URL 15-min TTL → on 403/load-error, force re-fetch.
  // Phase 1 simplification: bump reloadKey, the backend will return a fresh URL
  // on the next parent thread poll (5 s) — here we only redraw the <img>.
  if (errored) {
    return (
      <div className="mt-2 rounded-sm border border-amber-400/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300">
        <div>{t('porter.thread.message.attachmentUnavailable')}</div>
        <button
          type="button"
          className="mt-1 underline hover:opacity-80"
          onClick={() => {
            setErrored(false);
            setReloadKey(k => k + 1);
          }}
        >
          {t('porter.thread.error.retry')}
        </button>
      </div>
    );
  }

  return (
    <img
      key={reloadKey}
      src={attachment.url}
      alt={attachment.name}
      className="mt-2 max-h-64 rounded-sm border border-slate-500/20"
      onError={() => setErrored(true)}
    />
  );
}

function SyncStatusPill({ status }: { status: OmniMessage['syncStatus'] }) {
  const { t } = useTranslation();
  if (status === 'PENDING_SYNC') {
    return (
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-slate-500/15 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
        {t('porter.thread.message.pending')}
      </span>
    );
  }
  if (status === 'FAILED') {
    return (
      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:text-rose-300">
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {t('porter.thread.message.failed')}
      </span>
    );
  }
  return null;
}

function MessageBubble({ message, onRetry }: { message: OmniMessage; onRetry?: () => void }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isOutbound = message.direction === 'OUTBOUND';
  const isSystem = message.direction === 'SYSTEM';
  const long = isLongMessage(message.content);
  const displayBody = long && !expanded ? truncateMessage(message.content) : message.content;
  const attachments = message.attachments;

  // SYSTEM: Chatwoot activity / template event — center-aligned, muted italic.
  // Not a human reply, must visually distinct from INBOUND/OUTBOUND bubbles.
  // (E-102 Phase 5 story 102-p5-1, ADR-102 v0.4 addendum.)
  if (isSystem) {
    return (
      <div className="flex justify-center mb-3" data-testid="message-bubble-system">
        <div
          className="max-w-[80%] px-3 py-1.5 text-xs italic text-muted-foreground"
          data-testid="message-bubble"
          data-direction="SYSTEM"
        >
          <span aria-hidden>ℹ&nbsp;</span>
          <span className="whitespace-pre-wrap">{displayBody}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`flex max-w-[75%] ${isOutbound ? 'flex-row-reverse' : 'flex-row'} items-end gap-2`}
      >
        <div
          className={`rounded-sm px-4 py-3 text-sm leading-relaxed shadow-sm ${
            isOutbound
              ? 'border border-teal-400/40 bg-teal-600/30 text-foreground backdrop-blur-sm'
              : 'border border-slate-500/30 bg-slate-800/60 text-slate-100 backdrop-blur-sm'
          } ${message.syncStatus === 'FAILED' ? 'border-rose-400/60' : ''}`}
          style={{ borderRadius: '2px' }}
          data-testid="message-bubble"
          data-sync-status={message.syncStatus ?? 'SYNCED'}
          data-direction={message.direction}
        >
          <div className="mb-1 flex items-center text-xs font-semibold opacity-70">
            <span>{message.authorName ?? ''}</span>
            <SyncStatusPill status={message.syncStatus} />
          </div>
          <div className="whitespace-pre-wrap">{displayBody}</div>
          {long && (
            <button
              type="button"
              onClick={() => setExpanded(e => !e)}
              className="mt-2 text-xs font-medium text-teal-600 underline hover:opacity-80 dark:text-teal-300"
            >
              {expanded ? t('porter.thread.message.collapse') : t('porter.thread.message.expand')}
            </button>
          )}
          {attachments.map((att, i) =>
            att.contentType.startsWith('image/') && att.url ? (
              <AttachmentImage key={i} attachment={att} />
            ) : null
          )}
          {message.syncStatus === 'FAILED' && onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 inline-flex items-center gap-1 rounded-sm border border-rose-400/60 bg-rose-500/10 px-2 py-0.5 text-xs font-medium text-rose-700 hover:bg-rose-500/20 dark:text-rose-300"
            >
              ↻ {t('porter.thread.message.retry')}
            </button>
          )}
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {formatRelativeTime(message.createdAt)}
        </span>
      </div>
    </div>
  );
}

function PartnerPanel({
  partnerId,
  onCreatePartner,
}: {
  partnerId: string | null;
  onCreatePartner: () => void;
}) {
  const { t } = useTranslation();
  if (!partnerId) {
    // Sally UX #1 — NOT an empty card; actionable CTA
    return (
      <aside className="flex w-80 shrink-0 flex-col border-l border-teal-500/20 p-4 kgc-card-bg">
        <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {t('porter.thread.partner.label')}
        </h3>
        <div
          className="rounded-lg border border-dashed border-teal-400/60 p-4 text-center"
          data-testid="partner-unknown"
        >
          <p className="text-muted-foreground">{t('porter.thread.partner.unknown')}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={onCreatePartner}
            data-testid="partner-create-cta"
          >
            {t('porter.thread.partner.createCta')}
          </Button>
        </div>
      </aside>
    );
  }

  return (
    <aside className="flex w-80 shrink-0 flex-col gap-4 border-l border-teal-500/20 p-4 kgc-card-bg">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {t('porter.thread.partner.label')}
      </h3>
      <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800/40">
        <div className="text-foreground">{partnerId}</div>
        <div className="mt-1 text-xs text-muted-foreground">
          {t('porter.thread.partner.timeline')}
        </div>
      </div>
    </aside>
  );
}

// ============================================
// PAGE
// ============================================

export function InboxThreadDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ threadId?: string }>();
  const threadId = params.threadId ?? '';

  const { data, isLoading, isError, refetch } = usePorterThread(threadId);
  const replyMutation = useReplyToThread(threadId);
  const patchMutation = usePatchThread(threadId);

  const [replyDraft, setReplyDraft] = useState('');
  const [optimisticReplies, setOptimisticReplies] = useState<OmniMessage[]>([]);

  // Merge server messages with still-pending optimistic replies. Once the
  // server echoes them back (via invalidation + refetch) we drop the matching
  // optimistic row by clientId.
  const messages = useMemo<OmniMessage[]>(() => {
    const serverMessages = data?.data.messages ?? [];
    if (optimisticReplies.length === 0) return serverMessages;
    const serverIds = new Set(serverMessages.map(m => m.id));
    const stillPending = optimisticReplies.filter(m => !serverIds.has(m.id));
    return [...serverMessages, ...stillPending];
  }, [data, optimisticReplies]);

  if (!threadId) {
    return (
      <div className="p-6 text-sm text-muted-foreground">{t('porter.thread.error.loadFail')}</div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
          {t('porter.thread.loading') || 'Betöltés...'}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center p-6">
        <div className="text-center">
          <p className="mb-2 text-sm text-muted-foreground">{t('porter.thread.error.loadFail')}</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {t('porter.thread.error.retry')}
          </Button>
        </div>
      </div>
    );
  }

  const thread = data.data;

  const handleSend = (): void => {
    const content = replyDraft.trim();
    if (content.length === 0) return;

    const clientId = `optimistic-${Date.now()}`;
    const optimistic: OmniMessage = {
      id: clientId,
      threadId: thread.id,
      direction: 'OUTBOUND',
      content,
      contentHtml: null,
      authorUserId: null,
      authorName: null,
      syncStatus: 'PENDING_SYNC',
      attachments: [],
      createdAt: new Date().toISOString(),
    };
    setOptimisticReplies(prev => [...prev, optimistic]);
    setReplyDraft('');

    replyMutation.mutate(
      { content },
      {
        onSuccess: response => {
          // Replace optimistic row with synced id so it persists until server echo arrives
          setOptimisticReplies(prev =>
            prev.map(m =>
              m.id === clientId
                ? { ...m, id: response.data.id, syncStatus: response.data.syncStatus }
                : m
            )
          );
        },
        onError: () => {
          setOptimisticReplies(prev =>
            prev.map(m => (m.id === clientId ? { ...m, syncStatus: 'FAILED' } : m))
          );
        },
      }
    );
  };

  const handleRetryOptimistic = (clientId: string): void => {
    const failed = optimisticReplies.find(m => m.id === clientId);
    if (!failed) return;
    setOptimisticReplies(prev =>
      prev.map(m => (m.id === clientId ? { ...m, syncStatus: 'PENDING_SYNC' } : m))
    );
    replyMutation.mutate(
      { content: failed.content },
      {
        onSuccess: response => {
          setOptimisticReplies(prev =>
            prev.map(m =>
              m.id === clientId
                ? { ...m, id: response.data.id, syncStatus: response.data.syncStatus }
                : m
            )
          );
        },
        onError: () => {
          setOptimisticReplies(prev =>
            prev.map(m => (m.id === clientId ? { ...m, syncStatus: 'FAILED' } : m))
          );
        },
      }
    );
  };

  const handleStatusChange = (status: ThreadStatus): void => {
    patchMutation.mutate({ status });
  };

  const handleAssigneeChange = (assigneeUserId: string | null): void => {
    patchMutation.mutate({ assigneeUserId });
  };

  const handleCreatePartner = (): void => {
    // Phase 2: open partner-create modal prefilled from thread.subject / sender.
    // Phase 1 stub: route to partner list so the user can land somewhere
    // instead of hitting a dead button.
    navigate('/partners?new=1');
  };

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 border-b border-teal-500/20 px-4 py-2 text-sm text-muted-foreground kgc-card-bg">
        <span>📬 Porter</span>
        <span>/</span>
        <button
          type="button"
          onClick={() => navigate('/porter/inbox')}
          className="hover:text-foreground hover:underline"
        >
          {t('porter.thread.inboxLink') || 'Beárkezett'}
        </button>
        <span>/</span>
        <span className="font-medium text-foreground">{thread.subject ?? thread.id}</span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main thread column */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Thread meta header */}
          <div className="border-b border-teal-500/20 p-4 kgc-card-bg">
            <div className="mb-2 flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => navigate('/porter/inbox')}>
                ← {t('porter.thread.back') || 'Vissza'}
              </Button>
              <ChannelChip type={thread.channel} />
              <span className="text-muted-foreground">•</span>
              <StatusSelect
                value={thread.status}
                onChange={handleStatusChange}
                disabled={patchMutation.isPending}
              />
              <span className="text-muted-foreground">•</span>
              <AssigneePills value={thread.assigneeUserId} onChange={handleAssigneeChange} />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-base font-semibold text-foreground">
                {thread.subject ?? t('porter.thread.subject')}
              </div>
            </div>
          </div>

          {/* Message list */}
          <div
            className="flex-1 overflow-y-auto p-6"
            style={{
              background: 'linear-gradient(180deg, rgba(20,184,166,0.03) 0%, transparent 100%)',
            }}
            data-testid="message-list"
          >
            {messages.map(msg => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onRetry={
                  msg.syncStatus === 'FAILED' && msg.id.startsWith('optimistic-')
                    ? () => handleRetryOptimistic(msg.id)
                    : undefined
                }
              />
            ))}
          </div>

          {/* Reply composer — sticky bottom */}
          <div className="border-t border-teal-500/20 p-4 kgc-card-bg">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
              📝 {t('porter.thread.composer.placeholder')}
            </div>
            <div
              className="rounded-sm border border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900/60"
              style={{ borderRadius: '2px' }}
            >
              <textarea
                value={replyDraft}
                onChange={e => setReplyDraft(e.target.value)}
                placeholder={t('porter.thread.composer.placeholder')}
                className="h-24 w-full resize-none bg-transparent p-3 text-sm outline-none placeholder:text-muted-foreground"
                aria-label={t('porter.thread.composer.placeholder')}
              />
            </div>
            <div className="mt-3 flex items-center justify-end">
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: TEAL }}
                disabled={replyMutation.isPending || replyDraft.trim().length === 0}
                onClick={handleSend}
                data-testid="reply-send"
              >
                {replyMutation.isPending
                  ? t('porter.thread.composer.sending')
                  : t('porter.thread.composer.send')}{' '}
                →
              </Button>
            </div>
          </div>
        </div>

        {/* Partner context sidebar */}
        <PartnerPanel partnerId={thread.partnerId} onCreatePartner={handleCreatePartner} />
      </div>
    </div>
  );
}
